<template>
  <div class="resource-delivery">
    <div class="title-en">搜索条件</div>
    <el-card class="box-card">
      <div class="search-container">
        <el-form :inline="true" :model="searchForm" class="search-form" label-width="100px">
          <el-row v-for="(row, rowIndex) in searchFieldRows" :key="'search-row-' + rowIndex" :gutter="20">
            <el-col v-for="field in row" :key="field.key" :span="6">
              <el-form-item :label="field.label">
                <el-date-picker v-if="field.type === 'month'" v-model="searchForm[field.key]" type="month"
                  :placeholder="field.placeholder || '请选择月份'" format="yyyy-MM" value-format="yyyy-MM" clearable
                  style="width: 100%;">
                </el-date-picker>
                <el-input v-else v-model="searchForm[field.key]" :placeholder="field.placeholder"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <div class="search-button">
                <el-button type="primary" @click="handleSearch">点击查询</el-button>
                <el-button @click="handleReset">重置</el-button>
                <el-button @click="debouncedExport">{{ exportButtonText }}</el-button>
                <el-button type="success" plain @click="openBatchExportDialog">批量导出</el-button>
              </div>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </el-card>
    <BatchMonthExportDialog :visible.sync="showBatchExportDialog" :loading="batchExportLoading"
      @confirm="onBatchMonthExportConfirm" />
    <div class="title-en">{{ config.title }}</div>

    <el-card class="box-card">
      <div class="table-container">
        <div class="button-container">
          <el-button type="primary" @click="openUploadDialog">
            <i class="el-icon-upload"></i> {{ config.uploadButtonText }}
          </el-button>
          <el-button @click="openColumnsDialog">表头设置</el-button>
        </div>
        <el-table :data="faults" border stripe @selection-change="handleSelectionChange" ref="table"
          v-loading="loading">
          <el-table-column type="selection" width="55" align="center"></el-table-column>
          <el-table-column v-for="(column, index) in visibleColumns" :key="index" :prop="column.prop"
            :label="column.label" :width="column.width" :header-style="{
              'white-space': 'normal',
              'word-break': 'break-all',
              height: 'auto',
              'line-height': '1.2',
              padding: '8px 0',
            }" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="isNumericProp(column.prop)">{{
                scope.row[column.prop] === 0 ? 0 : (scope.row[column.prop] || 0)
              }}</span>
              <span v-else-if="isDatetimeProp(column.prop)">{{
                scope.row[column.prop]
                  ? $moment(scope.row[column.prop]).format('YYYY-MM-DD HH:mm:ss')
                  : '-'
              }}</span>
              <span v-else>{{ scope.row[column.prop] || '-' }}</span>
            </template>
          </el-table-column>
        </el-table>

        <el-dialog title="设置显示项" :visible.sync="showColumnsDialog" :append-to-body="true" width="600px"
          :close-on-click-modal="false" custom-class="columns-dialog" :modal-append-to-body="false"
          :destroy-on-close="true">
          <el-checkbox-group v-model="selectedColumns" class="checkbox-group">
            <el-checkbox v-for="column in columns" :key="column.prop" :label="column.prop" class="checkbox-item">
              {{ column.label }}
            </el-checkbox>
          </el-checkbox-group>
          <div slot="footer" class="dialog-footer">
            <el-button size="small" @click="selectAllColumns">全选</el-button>
            <el-button size="small" @click="restoreDefaultColumns">恢复默认</el-button>
            <el-button size="small" type="primary" @click="saveColumnsDialog">保存</el-button>
          </div>
        </el-dialog>

        <el-dialog :title="config.uploadTitle" :visible.sync="showUploadDialog" :append-to-body="true" width="600px"
          :close-on-click-modal="false" :destroy-on-close="true">
          <el-form :model="uploadForm" label-width="150px" :rules="uploadRules" ref="uploadForm">
            <el-form-item label="所属月份" prop="Month">
              <el-date-picker v-model="uploadForm.Month" type="month" placeholder="请选择月份" format="yyyy-MM"
                value-format="yyyy-MM" style="width: 100%;">
              </el-date-picker>
            </el-form-item>
            <el-form-item label="Excel文件" prop="file">
              <el-upload ref="uploadRef" action="#" :auto-upload="false" :on-change="handleFileChange"
                :on-remove="handleFileRemove" :on-exceed="handleFileExceed" :file-list="fileList" accept=".xlsx,.xls"
                :limit="1" drag class="upload-drag">
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                <div slot="tip" class="el-upload__tip">只能上传 .xlsx/.xls 格式的Excel文件，仅支持单个文件</div>
              </el-upload>
            </el-form-item>
          </el-form>
          <div slot="footer" class="dialog-footer">
            <el-button @click="showUploadDialog = false">取消</el-button>
            <el-button type="primary" @click="submitUpload" :loading="uploadLoading">上传</el-button>
          </div>
        </el-dialog>

        <div class="pagination-container">
          <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
            :current-page="currentPage" :page-sizes="[10, 20, 30, 50]" :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper" :total="total" />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import {
  overtimeBillList,
  tempStaffBillList,
  travelBillList,
  uploadOvertimeBill,
  uploadTempStaffBill,
  uploadTravelBill,
} from '@/api/appManage';
import * as XLSX from 'xlsx';
import { debounce } from 'lodash';
import batchMonthExport from '@/mixins/batchMonthExport';

const BILL_CONFIG = {
  travel: {
    title: '差旅账单列表',
    uploadTitle: '上传差旅账单信息',
    uploadButtonText: '上传差旅账单信息',
    sheetName: '差旅账单列表',
    listApi: travelBillList,
    uploadApi: uploadTravelBill,
    searchFields: [
      { key: 'Month', label: '月份', type: 'month', placeholder: '请选择月份' },
      { key: 'ticketId', label: '工单号', type: 'input', placeholder: '请输入工单号' },
      { key: 'campusName', label: '园区', type: 'input', placeholder: '请输入园区' },
      { key: 'applicant', label: '申请人', type: 'input', placeholder: '请输入申请人' },
      { key: 'specialType', label: '专项类型', type: 'input', placeholder: '请输入专项类型' },
    ],
    numericProps: ['subsidyDays', 'lodgingDays'],
    datetimeProps: ['startTime', 'endTime', 'createTime'],
    defaultSelectedColumns: [
      'ticketId',
      'applicant',
      'campusName',
      'specialType',
      'serviceMemo',
      'startTime',
      'endTime',
      'subsidyDays',
      'lodgingDays',
      'Month',
    ],
    columns: [
      { prop: 'ticketId', label: '工单号', width: 150 },
      { prop: 'applicant', label: '申请人', width: 180 },
      { prop: 'campusName', label: '园区', width: 140 },
      { prop: 'specialType', label: '专项类型', width: 220 },
      { prop: 'serviceMemo', label: '服务说明', width: 220 },
      { prop: 'startTime', label: '开始时间', width: 170 },
      { prop: 'endTime', label: '结束时间', width: 170 },
      { prop: 'subsidyDays', label: '补贴天数', width: 100 },
      { prop: 'lodgingDays', label: '住宿天数', width: 100 },
      { prop: 'Month', label: '所属计费月份', width: 120 },
      { prop: 'id', label: 'ID', width: 80 },
      { prop: 'createTime', label: '入库时间', width: 170 },
    ],
  },
  overtime: {
    title: '加班账单列表',
    uploadTitle: '上传加班账单信息',
    uploadButtonText: '上传加班账单信息',
    sheetName: '加班账单列表',
    listApi: overtimeBillList,
    uploadApi: uploadOvertimeBill,
    searchFields: [
      { key: 'Month', label: '月份', type: 'month', placeholder: '请选择月份' },
      { key: 'ticketId', label: '企微单号', type: 'input', placeholder: '请输入企微单号' },
      { key: 'demandNo', label: '需求单号', type: 'input', placeholder: '请输入需求单号' },
      { key: 'campusName', label: '园区', type: 'input', placeholder: '请输入园区' },
      { key: 'applicant', label: '申请人', type: 'input', placeholder: '请输入申请人' },
      { key: 'overtimeType', label: '加班类型', type: 'input', placeholder: '请输入加班类型' },
    ],
    numericProps: ['billValue'],
    datetimeProps: ['startTime', 'endTime', 'createTime'],
    defaultSelectedColumns: [
      'ticketId',
      'demandNo',
      'campusName',
      'applicant',
      'overtimeType',
      'overtimeMemo',
      'startTime',
      'endTime',
      'billValue',
      'reviewMemo',
      'Month',
    ],
    columns: [
      { prop: 'ticketId', label: '企微单号', width: 150 },
      { prop: 'demandNo', label: '需求单号', width: 160 },
      { prop: 'campusName', label: '园区', width: 140 },
      { prop: 'applicant', label: '申请人', width: 180 },
      { prop: 'overtimeType', label: '加班类型', width: 140 },
      { prop: 'overtimeMemo', label: '加班说明', width: 240 },
      { prop: 'startTime', label: '开始时间', width: 170 },
      { prop: 'endTime', label: '结束时间', width: 170 },
      { prop: 'billValue', label: '计费值', width: 90 },
      { prop: 'reviewMemo', label: '审查', width: 180 },
      { prop: 'Month', label: '所属计费月份', width: 120 },
      { prop: 'id', label: 'ID', width: 80 },
      { prop: 'createTime', label: '入库时间', width: 170 },
    ],
  },
  tempStaff: {
    title: '临时人力账单列表',
    uploadTitle: '上传临时人力账单信息',
    uploadButtonText: '上传临时人力账单信息',
    sheetName: '临时人力账单列表',
    listApi: tempStaffBillList,
    uploadApi: uploadTempStaffBill,
    searchFields: [
      { key: 'Month', label: '月份', type: 'month', placeholder: '请选择月份' },
      { key: 'ticketId', label: '工单号', type: 'input', placeholder: '请输入工单号' },
      { key: 'campusName', label: '园区', type: 'input', placeholder: '请输入园区' },
      { key: 'specialType', label: '专项类型', type: 'input', placeholder: '请输入专项类型' },
    ],
    numericProps: ['billValue'],
    datetimeProps: ['startTime', 'endTime', 'createTime'],
    defaultSelectedColumns: [
      'ticketId',
      'campusName',
      'specialType',
      'serviceMemo',
      'startTime',
      'endTime',
      'billValue',
      'billUnit',
      'Month',
    ],
    columns: [
      { prop: 'ticketId', label: '工单号', width: 160 },
      { prop: 'campusName', label: '园区', width: 140 },
      { prop: 'specialType', label: '专项类型', width: 140 },
      { prop: 'serviceMemo', label: '服务说明', width: 280 },
      { prop: 'startTime', label: '开始时间', width: 170 },
      { prop: 'endTime', label: '结束时间', width: 170 },
      { prop: 'billValue', label: '计费值', width: 90 },
      { prop: 'billUnit', label: '计费单位', width: 100 },
      { prop: 'Month', label: '所属计费月份', width: 120 },
      { prop: 'id', label: 'ID', width: 80 },
      { prop: 'createTime', label: '入库时间', width: 170 },
    ],
  },
};

function lastMonthStr() {
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  return lastMonth.getFullYear() + '-' + String(lastMonth.getMonth() + 1).padStart(2, '0');
}

export default {
  name: 'ExtraBillList',
  mixins: [batchMonthExport],
  props: {
    billKey: {
      type: String,
      required: true,
    },
  },
  data() {
    const config = BILL_CONFIG[this.billKey];
    const searchForm = {
      page: 1,
      size: 10,
    };
    (config.searchFields || []).forEach((field) => {
      searchForm[field.key] = field.key === 'Month' ? lastMonthStr() : '';
    });
    return {
      config,
      debouncedExport: null,
      faults: [],
      searchForm,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showColumnsDialog: false,
      selectedColumns: [...config.defaultSelectedColumns],
      defaultSelectedColumns: [...config.defaultSelectedColumns],
      columns: config.columns,
      visibleColumns: [],
      selectedRows: [],
      loading: false,
      showUploadDialog: false,
      uploadForm: {
        Month: '',
        file: null,
      },
      uploadRules: {
        Month: [{ required: true, message: '请选择月份', trigger: 'change' }],
        file: [{ required: true, message: '请选择文件', trigger: 'change' }],
      },
      fileList: [],
      uploadLoading: false,
    };
  },
  computed: {
    searchFieldRows() {
      const fields = this.config.searchFields || [];
      const rows = [];
      for (let i = 0; i < fields.length; i += 4) {
        rows.push(fields.slice(i, i + 4));
      }
      return rows;
    },
    exportButtonText() {
      return this.selectedRows.length > 0 ? `导出已选(${this.selectedRows.length})` : '导出全部';
    },
  },
  created() {
    this.visibleColumns = this.columns.filter((column) => this.selectedColumns.includes(column.prop));
    this.debouncedExport = debounce(this.handleExport, 300);
  },
  methods: {
    isNumericProp(prop) {
      return (this.config.numericProps || []).includes(prop);
    },
    isDatetimeProp(prop) {
      return (this.config.datetimeProps || []).includes(prop);
    },
    buildQueryParams(extra = {}) {
      const params = {
        ...extra,
        page: this.currentPage,
        size: this.pageSize,
      };
      (this.config.searchFields || []).forEach((field) => {
        params[field.key] = this.searchForm[field.key];
      });
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      return params;
    },
    async fetchFaultData() {
      this.loading = true;
      try {
        const response = await this.config.listApi(this.buildQueryParams());
        if (response.data) {
          this.faults = response.data.data || [];
          this.total = response.data.total || 0;
        }
      } catch (error) {
        console.error('请求错误:', error);
        let errorMessage = '获取数据失败';
        if (error.response && error.response.status === 422) {
          errorMessage = '请求参数格式不正确，请检查输入';
        }
        this.$message.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchFaultData();
    },
    handleReset() {
      (this.config.searchFields || []).forEach((field) => {
        this.searchForm[field.key] = field.key === 'Month' ? lastMonthStr() : '';
      });
      this.searchForm.page = 1;
      this.searchForm.size = 10;
      this.currentPage = 1;
      this.fetchFaultData();
    },
    openColumnsDialog() {
      this.showColumnsDialog = true;
    },
    saveColumnsDialog() {
      this.showColumnsDialog = false;
      this.visibleColumns = this.columns.filter((column) => this.selectedColumns.includes(column.prop));
    },
    selectAllColumns() {
      this.selectedColumns = this.columns.map((column) => column.prop);
    },
    restoreDefaultColumns() {
      this.selectedColumns = [...this.defaultSelectedColumns];
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.fetchFaultData();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchFaultData();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    formatExportValue(row, column) {
      let value = row[column.prop];
      if (this.isDatetimeProp(column.prop)) {
        return value ? this.$moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
      }
      if (this.isNumericProp(column.prop)) {
        return value === 0 ? 0 : value || '-';
      }
      return value !== null && value !== undefined && value !== '' ? value : '-';
    },
    rowsToWorksheetData(rows) {
      return rows.map((row) => {
        const rowData = {};
        this.visibleColumns.forEach((column) => {
          if (column.prop !== 'operation') {
            rowData[column.label] = this.formatExportValue(row, column);
          }
        });
        return rowData;
      });
    },
    writeWorkbook(worksheetData, filename) {
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const colWidths = {};
      this.visibleColumns.forEach((column, index) => {
        const width = column.width ? Math.ceil(parseInt(column.width, 10) / 5) + 2 : 16;
        colWidths[XLSX.utils.encode_col(index)] = { wch: width };
      });
      worksheet['!cols'] = Object.values(colWidths);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, this.config.sheetName);
      XLSX.writeFile(workbook, filename);
    },
    async handleExport(options = {}) {
      const { Month_start, Month_end, forceAll } = options || {};
      const isBatchRange = Boolean(Month_start && Month_end);
      this.exportLoading = true;
      try {
        if (forceAll || this.selectedRows.length === 0) {
          this.loading = true;
          const params = this.buildQueryParams({ export_excel: 1 });
          delete params.page;
          delete params.size;
          if (isBatchRange) {
            delete params.Month;
            params.Month_start = Month_start;
            params.Month_end = Month_end;
          }
          const response = await this.config.listApi(params);
          this.loading = false;
          const allData = (response.data && response.data.data) || [];
          const timestamp = this.$moment().format('YYYYMMDDHHmmss');
          const filename = isBatchRange
            ? `${this.config.sheetName}_${Month_start}_${Month_end}_${timestamp}.xlsx`
            : `${this.config.sheetName}_${timestamp}.xlsx`;
          this.writeWorkbook(this.rowsToWorksheetData(allData), filename);
          this.$message.success('导出成功');
        } else {
          const timestamp = this.$moment().format('YYYYMMDDHHmmss');
          this.writeWorkbook(
            this.rowsToWorksheetData(this.selectedRows),
            `${this.config.sheetName}_${timestamp}.xlsx`
          );
          this.$message.success('导出成功');
        }
      } catch (error) {
        console.error('导出错误:', error);
        this.$message.error('导出失败，请重试');
      } finally {
        this.exportLoading = false;
        this.loading = false;
      }
    },
    openUploadDialog() {
      this.showUploadDialog = true;
      this.uploadForm = {
        Month: this.searchForm.Month || '',
        file: null,
      };
      this.fileList = [];
    },
    handleFileChange(file, fileList) {
      this.fileList = fileList;
      this.uploadForm.file = file.raw;
    },
    handleFileRemove(file, fileList) {
      this.fileList = fileList;
      if (fileList.length === 0) {
        this.uploadForm.file = null;
      }
    },
    handleFileExceed(files) {
      this.$refs.uploadRef.clearFiles();
      const file = files[0];
      this.$refs.uploadRef.handleStart(file);
      this.fileList = [{ name: file.name, raw: file, status: 'ready' }];
      this.uploadForm.file = file;
      this.$message.info('已自动替换为新文件');
    },
    async submitUpload() {
      this.$refs.uploadForm.validate(async (valid) => {
        if (!valid) {
          return;
        }
        if (!this.uploadForm.file) {
          this.$message.error('请选择要上传的文件');
          return;
        }
        this.uploadLoading = true;
        try {
          const formData = new FormData();
          formData.append('file', this.uploadForm.file);
          formData.append('Month', this.uploadForm.Month);
          const response = await this.config.uploadApi(formData);
          if (response.data && response.data.code === 200) {
            this.$message.success(response.data.msg || '上传成功');
            this.showUploadDialog = false;
            this.handleSearch();
          } else {
            this.$message.error(response.data?.msg || '上传失败');
          }
        } catch (error) {
          console.error('上传错误:', error);
          let errorMessage = '上传失败';
          if (error.response && error.response.data && error.response.data.msg) {
            errorMessage = error.response.data.msg;
          }
          this.$message.error(errorMessage);
        } finally {
          this.uploadLoading = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.resource-delivery {
  padding: 0;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 30px;
}

.search-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
}

.search-form {
  width: 100%;
}

.search-button {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-left: 13px;
}

.el-form-item {
  margin-bottom: 18px;
  display: flex;
}

.button-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
  position: relative;
}

.box-card {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
}

.title-en {
  margin: 10px 0;
  padding-left: 10px;
  border-left: 3px solid #219aff;
  font-weight: normal;
}

.columns-dialog {
  margin-top: 0 !important;
  position: absolute !important;
  margin-left: 0 !important;
  top: 100% !important;
  left: 0 !important;
  transform: none !important;
}

.el-dialog__wrapper.columns-dialog {
  position: absolute !important;
  overflow: visible !important;
}

.checkbox-group {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 10px;
  max-height: 300px;
  overflow-y: auto;
}

.checkbox-item {
  margin-right: 0;
  white-space: normal;
  word-break: break-all;
  line-height: 1.2;
  padding: 5px 0;
}

.dialog-footer {
  text-align: right;
  padding-top: 15px;
  border-top: 1px solid #ebeef5;
}

.dialog-footer .el-button+.el-button {
  margin-left: 8px;
}

::v-deep .el-date-editor.el-input__inner,
.el-date-editor--daterange.el-input__inner {
  width: 100% !important;
}

::v-deep .el-range-editor.el-input__inner {
  width: 100% !important;
}

::v-deep .el-date-editor .el-range-input {
  width: 66% !important;
}

::v-deep .el-range-editor--small .el-range-input {
  font-size: 12px !important;
}

::v-deep .el-select,
.el-input {
  width: 100% !important;
}

::v-deep .upload-drag .el-upload {
  width: 100%;
}

::v-deep .upload-drag .el-upload-dragger {
  width: 100%;
  height: 120px;
}

::v-deep .upload-drag .el-icon-upload {
  font-size: 40px;
  margin-top: 10px;
}

::v-deep .el-form-item__content {
  width: calc(100% - 10px) !important;
}
</style>
